import * as Dialog from '@radix-ui/react-dialog'
import cookies from 'browser-cookies'
import { observer } from 'mobx-react-lite'
import { FC, useEffect, useState } from 'react'
import ReactGA from 'react-ga'
import { useAuth } from '../../../hooks/useAuth'
import { useDrafts, useDraftShareCode } from '../../../queries/drafts'
import { useTreeState } from '../../../stores/Root'
import { requiresUserLoggedIn } from '../../AuthFlow/AuthFlowModal'
import { userIsLoggedIn } from '../../core/Authentication'
import ClickToCopy from '../../core/ClickToCopy'
import { Box, Column, Row } from '../../core/Components'
import { FieldLabel } from '../../core/FieldLabel'
import { Modal } from '../../core/Modal'
import { Subtitle } from '../../core/Subtitle'
import { Title } from '../../core/Title'
import { StudioActivityBarIcon } from '../StudioActivityBar'
import { segmentStudioButton } from '../StudioUtils'

const Body: FC = observer(() => {
  const [shareLink, setShareLink] = useState<string>(null)
  const { studio } = useTreeState()
  const draftsQuery = useDrafts(
    { name: studio.song.name, perPage: 1 },
    { refetchOnMount: true, enabled: !!studio.song.name }
  )
  const draftShareCodeQuery = useDraftShareCode(draftsQuery?.data?.pages[0].data?.[0]?.id, {
    enabled: !!draftsQuery?.data?.pages[0].data?.[0]?.id,
    refetchOnMount: true
  })

  useEffect(() => {
    if (draftShareCodeQuery.data) {
      setShareLink(
        `${window.location.origin}/player?type=draft&draft=${draftShareCodeQuery.data.shareCode}`
      )
    }
  }, [draftShareCodeQuery])

  const isLoading = draftsQuery.isLoading || draftShareCodeQuery.isLoading
  const noDraftFound =
    !studio.song.name || (!draftsQuery.isLoading && draftsQuery.data.pages[0].data.length === 0)

  return (
    <Box css={{ width: '400px' }}>
      <Title>
        <img
          src="/static/icons/share_black.svg"
          className="mb-1"
          style={{ height: '24px', width: '24px' }}
        />
        <p>Share your song</p>
      </Title>
      <Column>
        <Subtitle>Use this link to share your song</Subtitle>
        <FieldLabel>Share link</FieldLabel>
        <input
          className="studio-modal-input w-100"
          value={
            cookies.get('session')
              ? isLoading && studio.song.name
                ? 'Loading...'
                : shareLink || ''
              : ''
          }
          contentEditable={false}
          disabled={!shareLink}
          readOnly={true}
        />
        <p className="error-text" style={{ marginBottom: '30px' }}>
          {cookies.get('session')
            ? noDraftFound
              ? 'You must save your song as a draft before sharing'
              : ''
            : 'Approve the signature in your wallet for your share link'}
        </p>
        <Row>
          <Dialog.Close className="studio-modal-button-outline me-3" style={{ width: '50%' }}>
            Cancel
          </Dialog.Close>
          <ClickToCopy copyContent={shareLink || ''} style={{ width: '50%' }}>
            <button
              type="button"
              className="studio-modal-button-fill d-flex justify-content-center align-items-center"
              disabled={isLoading || !shareLink}
              style={{ width: '100%' }}
            >
              Copy link
            </button>
          </ClickToCopy>
        </Row>
      </Column>
    </Box>
  )
})

const Trigger = () => {
  const { user } = useAuth()

  const onClick = (e) => {
    if (!userIsLoggedIn(user)) {
      ReactGA.event({
        category: 'Studio',
        action: 'Clicked Share',
        label: 'Not Logged In'
      })
      return
    } else {
      ReactGA.event({
        category: 'Studio',
        action: 'Clicked Share',
        label: 'Logged In'
      })
      segmentStudioButton('Share Button', 'studio activity bar')
    }
  }

  return (
    <StudioActivityBarIcon title="Share" onClick={onClick} src="/static/icons/share_white.svg" />
  )
}

const StudioShareComponent: FC = (props) => {
  const { user, isUserLoading } = useAuth()
  const trigger = requiresUserLoggedIn(<Trigger />)

  if (!user || isUserLoading) {
    return trigger
  }

  return (
    <Modal trigger={<Box>{trigger}</Box>}>
      <Body {...props} />
    </Modal>
  )
}

export default observer(StudioShareComponent)
